import React, {Component, ReactNode} from 'react'
import {MeetingRepositoryForAdmin} from '../MeetingRepositoryForAdmin'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import MeetingCommentView from '../model/MeetingCommentView'
import DeletedCommentView from '../model/DeletedCommentView'
import MeetingCommentReply from '../model/MeetingCommentReply'
import DeletedCommentReply from '../model/DeletedCommentReply'
import {MeetingCommentFile} from '../model/MeetingCommentFile'
import {Nullable, Optional} from '../../../app/TypeHelpers'
import {CenterRowFlexContainer, LeftRowFlexContainer, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEdit} from '@fortawesome/free-regular-svg-icons'
import {Box, Divider} from '@mui/material'
import {IconBox, MeetingCommentTypes, UserIconLarge} from '../AssocDirectorCommon'
import {faAngleDown, faAngleUp, faReply} from '@fortawesome/free-solid-svg-icons'
import MeetingCommentDetail from '../model/MeetingCommentDetail'
import DeletedCommentDetail from '../model/DeletedCommentDetail'
import {AsyncCommunicationProps} from '../../shared/hoc/withAsyncCommunication'
import AdminMeetingCommentComponent, {UserNameDiv} from './AdminMeetingCommentComponent'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import styled from 'styled-components'
import AdminMeetingCommentInputForm, {InputError} from './AdminMeetingCommentInputForm'
import NewMeetingComment from '../model/NewMeetingComment'
import MeetingComment, {CommentReactedUser} from '../model/MeetingComment'
import UpdateMeetingComment from '../model/UpdateMeetingComment'
import {MESSAGE_TYPE, ROLE_FOR_ADMINISTRATOR} from '../../../CodeMaster'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {SubmitNewCommentProcedureForAdmin} from '../model/SubmitNewCommentProcedureForAdmin'
import {SubmitNewCommentReplyProcedureForAdmin} from '../model/SubmitNewCommentReplyProcedureForAdmin'
import {SubmitUpdateCommentProcedureForAdmin} from '../model/SubmitUpdateCommentProcedureForAdmin'
import {SubmitUpdateCommentReplyProcedureForAdmin} from '../model/SubmitUpdateCommentReplyProcedureForAdmin'
import {FileUploadError} from '../model/SubmitCommentAndFilesTemplate'
import {AdminPrimaryOutlinedButton} from '../../../styledComponents/adminPage/Buttons'
import {CommentNotificationData} from '../model/CommentNotificationData'
import {
    AdminLoginInfoSessionStorageKey,
} from '../../../__NoAutomatedTests/common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {MeetingHelper} from '../utils/MeetingHelper'

interface CommentReplyListDictionary {
    [replySourceId: string]: (MeetingCommentReply | DeletedCommentReply)[]
}

interface CommentChunk {
    commentList: (MeetingCommentView | DeletedCommentView)[]
    commentReplyListDictionary: CommentReplyListDictionary
}

export interface InputCommentContent {
    inputContent: string
    inputFileList: MeetingCommentFile[]
    deleteFileNumberList: number[]
    commentInputError: Optional<InputError>
}

interface Props {
    condominiumCd: string
    meetingId: number
    meetingRepoForAdmin: MeetingRepositoryForAdmin
    eventEmitter: EventEmitterExAdmin
    havePostCommentAuthority: boolean
    currentDatetime: string
}

type InputCommentTargetTypes = Nullable<number | 'new' | MeetingComment | MeetingCommentReply>

interface State {
    commentList: (MeetingCommentView | DeletedCommentView)[]
    commentReplyListDictionary: CommentReplyListDictionary
    openedCommentIds: Set<number>
    inputCommentTarget: InputCommentTargetTypes
    inputContent: string
    inputFileList: MeetingCommentFile[]
    deleteFileNumberList: number[]
    commentInputError: Optional<InputError>
}

export class AdminMeetingCommentArea extends Component<Props & AsyncCommunicationProps, State> {
    submitNewCommentProcedureForAdmin: SubmitNewCommentProcedureForAdmin
    submitNewCommentReplyProcedureForAdmin: SubmitNewCommentReplyProcedureForAdmin
    submitUpdateCommentProcedureForAdmin: SubmitUpdateCommentProcedureForAdmin
    submitUpdateCommentReplyProcedureForAdmin: SubmitUpdateCommentReplyProcedureForAdmin

    constructor(props: Readonly<Props & AsyncCommunicationProps>) {
        super(props)

        const setInputError = (inputError: InputError) => {
            this.setState({
                commentInputError: inputError,
            })
        }

        this.submitNewCommentProcedureForAdmin = new SubmitNewCommentProcedureForAdmin(
            setInputError,
            this.props.meetingRepoForAdmin,
            this.props.asyncCallFunction,
            this.props.eventEmitter,
        )

        this.submitNewCommentReplyProcedureForAdmin = new SubmitNewCommentReplyProcedureForAdmin(
            setInputError,
            this.props.meetingRepoForAdmin,
            this.props.asyncCallFunction,
            this.props.eventEmitter,
        )

        this.submitUpdateCommentProcedureForAdmin = new SubmitUpdateCommentProcedureForAdmin(
            setInputError,
            this.props.meetingRepoForAdmin,
            this.props.asyncCallFunction,
            this.props.eventEmitter,
        )

        this.submitUpdateCommentReplyProcedureForAdmin = new SubmitUpdateCommentReplyProcedureForAdmin(
            setInputError,
            this.props.meetingRepoForAdmin,
            this.props.asyncCallFunction,
            this.props.eventEmitter,
        )

        this.state = {
            commentList: [],
            commentReplyListDictionary: {},
            openedCommentIds: new Set<number>(),
            inputCommentTarget: null,
            inputContent: '',
            inputFileList: [],
            deleteFileNumberList: [],
            commentInputError: undefined,
        }
    }

    async componentDidMount(): Promise<void> {
        await this.reset()
    }

    async componentDidUpdate(
        prevProps: Readonly<Props & AsyncCommunicationProps>,
    ): Promise<void> {
        if (this.props.meetingId === prevProps.meetingId) {
            return
        }
        await this.reset()
    }

    async reset(): Promise<void> {
        const commentList = await this.getCommentList()
        if (commentList === null) {
            return
        }

        this.setState({
            commentList: commentList,
            commentReplyListDictionary: {},
            openedCommentIds: new Set<number>(),
            inputCommentTarget: null,
            inputContent: '',
            inputFileList: [],
            deleteFileNumberList: [],
            commentInputError: undefined,
        })
    }

    async getCommentList(): Promise<Nullable<(MeetingCommentView | DeletedCommentView)[]>> {
        const {condominiumCd, meetingId, meetingRepoForAdmin, asyncCallFunction, eventEmitter} = this.props

        let commentList: Nullable<(MeetingCommentView | DeletedCommentView)[]>
        await eventEmitter.showLoading()
        try {
            commentList = await asyncCallFunction(
                meetingRepoForAdmin.getComments.bind(
                    meetingRepoForAdmin,
                    condominiumCd,
                    meetingId,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        } finally {
            eventEmitter.closeLoading()
        }

        return commentList
    }

    async getCommentReplyList(
        commentId: number,
        showLoadingSpinner: boolean = true,
    ): Promise<Nullable<(MeetingCommentReply | DeletedCommentReply)[]>> {
        const {meetingRepoForAdmin, eventEmitter, condominiumCd, meetingId} = this.props

        let commentDetail: Nullable<MeetingCommentDetail | DeletedCommentDetail> = null
        if (showLoadingSpinner) {
            await eventEmitter.showLoading()
        }
        try {
            commentDetail = await this.props.asyncCallFunction(
                meetingRepoForAdmin.getCommentDetail.bind(
                    meetingRepoForAdmin,
                    condominiumCd,
                    meetingId,
                    commentId,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        } finally {
            if (showLoadingSpinner) {
                eventEmitter.closeLoading()
            }
        }

        if (commentDetail === null) {
            return null
        }

        return commentDetail.commentReplyList
    }

    isNewComment(inputCommentTarget: InputCommentTargetTypes): inputCommentTarget is 'new' {
        return inputCommentTarget === 'new'
    }

    isNewCommentReply(inputCommentTarget: InputCommentTargetTypes): inputCommentTarget is number {
        return typeof inputCommentTarget === 'number'
    }

    isEditComment(inputCommentTarget: InputCommentTargetTypes): inputCommentTarget is MeetingComment {
        return (inputCommentTarget instanceof MeetingComment)
            && !(inputCommentTarget instanceof MeetingCommentReply)
    }

    isEditCommentReply(inputCommentTarget: InputCommentTargetTypes): inputCommentTarget is MeetingCommentReply {
        return inputCommentTarget instanceof MeetingCommentReply
    }

    handleClickReaction = async (targetComment: MeetingCommentTypes, isReaction: boolean): Promise<Nullable<void>> => {
        const {
            condominiumCd,
            meetingId,
            meetingRepoForAdmin,
            asyncCallFunction,
        } = this.props

        let func: () => Promise<CommentReactedUser[]>
        if (targetComment instanceof MeetingCommentReply) {
            // @ts-ignore
            func = meetingRepoForAdmin.updateCommentReplyReaction.bind(
                meetingRepoForAdmin,
                condominiumCd,
                meetingId,
                targetComment.replySourceId,
                targetComment.meetingCommentId,
                isReaction,
            )
        } else {
            func = meetingRepoForAdmin.updateCommentReaction.bind(
                meetingRepoForAdmin,
                condominiumCd,
                meetingId,
                targetComment.meetingCommentId,
                isReaction,
            )
        }
        const rawCommentReactedUserList = await asyncCallFunction(func, (e) => {
            this.props.eventEmitter.showHeadingAreaMessage(e)
        })
        if (rawCommentReactedUserList === null) {
            return null
        }
        const commentReactedUserList = rawCommentReactedUserList

        if (targetComment instanceof MeetingCommentReply) {
            const newCommentReplyListDictionary: CommentReplyListDictionary = {}
            Object.entries(this.state.commentReplyListDictionary).forEach(([replySourceId, commentReplyList]) => {
                if (Number(replySourceId) === targetComment.replySourceId) {
                    newCommentReplyListDictionary[replySourceId] = commentReplyList.map(commentReply => {
                        if (commentReply.meetingCommentId === targetComment.meetingCommentId
                            && commentReply instanceof MeetingCommentReply) {
                            return new MeetingCommentReply(
                                commentReply.meetingCommentId,
                                commentReply.replySourceId,
                                commentReply.content,
                                commentReply.user,
                                commentReply.updateDatetime,
                                commentReactedUserList,
                                commentReply.attachmentList,
                                commentReply.isYou,
                                commentReply.version,
                            )
                        } else {
                            return commentReply
                        }
                    })
                } else {
                    newCommentReplyListDictionary[replySourceId] = commentReplyList
                }
            })
            this.setState({
                commentReplyListDictionary: newCommentReplyListDictionary,
            })
        } else {
            const newCommentList = this.state.commentList.map(comment => {
                if (comment.meetingCommentId === targetComment.meetingCommentId &&
                    comment instanceof MeetingCommentView) {

                    return new MeetingCommentView(
                        comment.meetingCommentId,
                        comment.content,
                        comment.user,
                        comment.updateDatetime,
                        commentReactedUserList,
                        comment.attachmentList,
                        comment.isYou,
                        comment.version,
                        comment.replyCount,
                        comment.latestReplyDatetime,
                    )
                } else {
                    return comment
                }
            })
            this.setState({
                commentList: newCommentList,
            })
        }
    }

    handleClickCommentDelete = async (comment: MeetingCommentTypes): Promise<void> => {
        const {
            condominiumCd,
            meetingId,
            meetingRepoForAdmin,
            eventEmitter,
            asyncCallFunction,
        } = this.props

        await eventEmitter.showLoading()
        try {
            let func: () => Promise<void>
            if (comment instanceof MeetingCommentReply) {
                func = meetingRepoForAdmin.deleteCommentReply.bind(
                    meetingRepoForAdmin,
                    condominiumCd,
                    meetingId,
                    comment.replySourceId,
                    comment.meetingCommentId,
                )
            } else {
                func = meetingRepoForAdmin.deleteComment.bind(
                    meetingRepoForAdmin,
                    condominiumCd,
                    meetingId,
                    comment.meetingCommentId,
                )
            }
            const result = await asyncCallFunction(func, (e) => {
                this.props.eventEmitter.showHeadingAreaMessage(e)
            })
            if (result === null) {
                return
            }

            const reloadedCommentChunk = await this.getShownComments(this.state.openedCommentIds)
            if (!reloadedCommentChunk) {
                return
            }

            this.setState({
                commentList: reloadedCommentChunk.commentList,
                commentReplyListDictionary: reloadedCommentChunk.commentReplyListDictionary,
            })

        } finally {
            eventEmitter.closeLoading()
        }
    }

    showDiscardInputAlertDialog(
        onOk: () => void,
        onCancel: () => void,
    ): void {
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: MESSAGE_TYPE.WARNING,
                message: '入力内容が破棄されます。よろしいですか？',
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: onCancel,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: onOk,
                    },
                ],
            },
        )
    }

    handleClickCommentEditButton = (
        comment: MeetingComment | MeetingCommentReply,
    ): void => {
        if (this.isInputContentsChanged()) {
            this.showDiscardInputAlertDialog(
                () => {
                    this.props.eventEmitter.closeDialog()
                    this.showCommentEditArea(comment)
                },
                () => {
                    this.props.eventEmitter.closeDialog()
                },
            )
        } else {
            this.showCommentEditArea(comment)
        }
    }

    showCommentEditArea = (
        comment: MeetingComment | MeetingCommentReply,
    ) => {
        const commentFileList: MeetingCommentFile[] = []
        comment.attachmentList.forEach(attachment =>
            commentFileList.push({
                fileNumber: attachment.id,
                fileData: attachment.uri,
                fileName: attachment.displayName,
                fileSize: 0,
            }),
        )

        this.closeInputArea()
        this.setState({
            inputCommentTarget: comment,
            inputContent: comment.content,
            inputFileList: commentFileList,
        })
    }

    handleClickViewRepliesButton = async (commentId: number): Promise<void> => {
        this.props.eventEmitter.clearHeadingAreaMessage()

        await this.openComment(commentId)
    }

    openComment = async (commentId: number): Promise<void> => {
        const {openedCommentIds, commentReplyListDictionary} = this.state
        if (openedCommentIds.has(commentId)) {
            return
        }

        const newOpenedCommentIds = new Set<number>(openedCommentIds)
        newOpenedCommentIds.add(commentId)
        this.setState({openedCommentIds: newOpenedCommentIds})

        if (commentReplyListDictionary[commentId] === undefined) {
            const newCommentReplyList = await this.getCommentReplyList(commentId)
            if (newCommentReplyList === null) {
                return
            }
            const newCommentReplyListDictionary = {
                ...commentReplyListDictionary,
                [`${commentId}`]: newCommentReplyList,
            }
            this.setState({
                commentReplyListDictionary: newCommentReplyListDictionary,
            })
        }
    }

    handleClickCloseRepliesButton = (commentId: number): void => {
        this.props.eventEmitter.clearHeadingAreaMessage()

        const {inputCommentTarget} = this.state
        let replySourceId: Nullable<number> = null
        if (this.isNewCommentReply(inputCommentTarget)) {
            replySourceId = inputCommentTarget
        } else if (this.isEditCommentReply(inputCommentTarget)) {
            replySourceId = inputCommentTarget.replySourceId
        }
        if (replySourceId === commentId) {
            if (this.isInputContentsChanged()) {
                this.showDiscardInputAlertDialog(
                    () => {
                        this.props.eventEmitter.closeDialog()
                        this.closeInputArea()
                        this.closeComment(commentId)
                    },
                    () => {
                        this.props.eventEmitter.closeDialog()
                    },
                )
            } else {
                this.closeInputArea()
                this.closeComment(commentId)
            }
        } else {
            this.closeComment(commentId)
        }
    }

    closeComment = (commentId: number) => {
        const {openedCommentIds} = this.state
        const newOpenedReplyComments = new Set<number>(openedCommentIds)
        newOpenedReplyComments.delete(commentId)
        this.setState({openedCommentIds: newOpenedReplyComments})
    }

    isInputContentsChanged(): boolean {
        const {inputCommentTarget, inputContent, inputFileList, deleteFileNumberList} = this.state

        const initialValue = (this.isEditComment(inputCommentTarget) || this.isEditCommentReply(inputCommentTarget))
            ? inputCommentTarget.content : ''
        if (inputContent !== initialValue) {
            return true
        }

        if (inputFileList.some(inputFile => inputFile.fileNumber === null)) {
            return true
        }

        if (deleteFileNumberList.length > 0) {
            return true
        }

        return false
    }

    closeInputArea(): void {
        this.setState({
            inputCommentTarget: null,
            inputContent: '',
            inputFileList: [],
            deleteFileNumberList: [],
            commentInputError: undefined,
        })
    }

    handleClickNewCommentButton = (): void => {
        this.props.eventEmitter.clearHeadingAreaMessage()

        if (this.isInputContentsChanged()) {
            this.showDiscardInputAlertDialog(
                () => {
                    this.props.eventEmitter.closeDialog()
                    this.showCommentNewArea()
                },
                () => {
                    this.props.eventEmitter.closeDialog()
                },
            )
        } else {
            this.showCommentNewArea()
        }
    }

    showCommentNewArea = () => {
        this.closeInputArea()
        this.setState({
            inputCommentTarget: 'new',
        })
    }

    handleClickReplyButton = (commentId: number): void => {
        this.props.eventEmitter.clearHeadingAreaMessage()

        if (this.isInputContentsChanged()) {
            this.showDiscardInputAlertDialog(
                () => {
                    this.props.eventEmitter.closeDialog()
                    this.showCommentReplyArea(commentId)
                },
                () => {
                    this.props.eventEmitter.closeDialog()
                },
            )
        } else {
            this.showCommentReplyArea(commentId)
        }
    }

    showCommentReplyArea = (commentId: number) => {
        this.closeInputArea()
        this.setState({
            inputCommentTarget: commentId,
        })
    }

    handleChangeInputForm = (
        content: string,
        commentFileList: MeetingCommentFile[],
        deleteFileNumberList: number[],
        inputError?: InputError,
    ): void => {
        this.setState({
            inputContent: content,
            inputFileList: commentFileList,
            deleteFileNumberList: deleteFileNumberList,
            commentInputError: inputError,
        })
    }

    refreshAfterSubmitComment = async (fileUploadError: Nullable<FileUploadError>): Promise<void> => {
        const {eventEmitter} = this.props
        const {inputCommentTarget, openedCommentIds} = this.state

        this.closeInputArea()

        let reloadedCommentChunk: Optional<CommentChunk>
        await eventEmitter.showLoading()
        try {
            const newOpenedCommentIds = new Set<number>(openedCommentIds)
            if (this.isNewCommentReply(inputCommentTarget)) {
                newOpenedCommentIds.add(inputCommentTarget)
            }
            reloadedCommentChunk = await this.getShownComments(newOpenedCommentIds)
            if (!reloadedCommentChunk) {
                return
            }

            this.setState({
                commentList: reloadedCommentChunk.commentList,
                commentReplyListDictionary: reloadedCommentChunk.commentReplyListDictionary,
                openedCommentIds: newOpenedCommentIds,
            })
        } finally {
            eventEmitter.closeLoading()
        }

        if (fileUploadError !== null) {
            const reloadedComment = this.getReloadedComment(
                inputCommentTarget,
                fileUploadError.commentId,
                reloadedCommentChunk,
            )
            if (!(reloadedComment instanceof MeetingComment)) {
                return
            }

            this.showCommentEditArea(reloadedComment)

            this.showFileUploadError(fileUploadError.errorFileNameList)
        }
    }

    getReloadedComment(
        inputCommentTarget: InputCommentTargetTypes,
        commentId: number,
        reloadedCommentChunk: CommentChunk,
    ): Optional<MeetingCommentTypes> {
        let comment: Optional<MeetingCommentTypes>

        if (this.isNewComment(inputCommentTarget)) {
            comment = reloadedCommentChunk.commentList
                .find(newComment => newComment.meetingCommentId === commentId)
        } else if (this.isNewCommentReply(inputCommentTarget)) {
            comment = reloadedCommentChunk.commentReplyListDictionary[`${inputCommentTarget}`]
                .find(replyComment => replyComment.meetingCommentId === commentId)
        } else if (this.isEditCommentReply(inputCommentTarget)) {
            comment = reloadedCommentChunk.commentReplyListDictionary[`${inputCommentTarget.replySourceId}`]
                .find(replyComment => replyComment.meetingCommentId === commentId)
        } else if (this.isEditComment(inputCommentTarget)) {
            comment = reloadedCommentChunk.commentList
                .find(newComment => newComment.meetingCommentId === commentId)
        }
        return comment
    }

    showFileUploadError = (
        errorFileNameList: string[],
    ): void => {
        const commentInputError: InputError = {
            title: '資料のアップロードに失敗しました。',
            messages: [
                'コメントを投稿できましたが、以下の資料のアップロード中に問題が発生しました。',
                '資料を再度登録してください。',
            ],
            fileNameList: errorFileNameList,
        }
        this.setState({
            commentInputError: commentInputError,
        })
    }

    handleClickSubmitButton = async (): Promise<void> => {
        await this.props.eventEmitter.showLoading()
        try {
            const {
                inputContent,
                inputFileList,
                inputCommentTarget,
                deleteFileNumberList,
            } = this.state

            let result: boolean = false
            let fileUploadError: Nullable<FileUploadError> = null
            if (this.isNewComment(inputCommentTarget)) {
                const commentId = await this.submitNewCommentProcedureForAdmin.submit(
                    this.props.condominiumCd,
                    this.props.meetingId,
                    new NewMeetingComment(inputContent),
                    inputFileList,
                )
                fileUploadError = this.submitNewCommentProcedureForAdmin.getFileUploadError()
                if (commentId) {
                    const commentNotificationData: CommentNotificationData = {
                        meetingId: this.props.meetingId,
                        commentId: commentId,
                    }

                    this.props.meetingRepoForAdmin.sendCommentNotification(
                        this.props.condominiumCd,
                        commentNotificationData,
                    ).then(r => null)
                    result = true
                }
            } else if (this.isNewCommentReply(inputCommentTarget)) {
                const commentId = await this.submitNewCommentReplyProcedureForAdmin.submit(
                    this.props.condominiumCd,
                    this.props.meetingId,
                    inputCommentTarget,
                    new NewMeetingComment(inputContent),
                    inputFileList,
                )
                fileUploadError = this.submitNewCommentReplyProcedureForAdmin.getFileUploadError()
                if (commentId) {
                    const commentNotificationData: CommentNotificationData = {
                        meetingId: this.props.meetingId,
                        commentId: commentId,
                    }

                    this.props.meetingRepoForAdmin.sendCommentNotification(
                        this.props.condominiumCd,
                        commentNotificationData,
                    ).then(r => null)
                    result = true
                }
            } else if (this.isEditCommentReply(inputCommentTarget)) {
                result = await this.submitUpdateCommentReplyProcedureForAdmin.submit(
                    this.props.condominiumCd,
                    this.props.meetingId,
                    inputCommentTarget.replySourceId,
                    inputCommentTarget.meetingCommentId,
                    new UpdateMeetingComment(inputContent, deleteFileNumberList, inputCommentTarget.version),
                    inputFileList,
                )
                fileUploadError = this.submitUpdateCommentReplyProcedureForAdmin.getFileUploadError()
            } else if (this.isEditComment(inputCommentTarget)) {
                result = await this.submitUpdateCommentProcedureForAdmin.submit(
                    this.props.condominiumCd,
                    this.props.meetingId,
                    inputCommentTarget.meetingCommentId,
                    new UpdateMeetingComment(inputContent, deleteFileNumberList, inputCommentTarget.version),
                    inputFileList,
                )
                fileUploadError = this.submitUpdateCommentProcedureForAdmin.getFileUploadError()
            }
            if (!result) {
                return
            }
            await this.refreshAfterSubmitComment(fileUploadError)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    getShownComments = async (
        openedCommentIds: Set<number>,
    ): Promise<Optional<CommentChunk>> => {
        const newCommentList = await this.getCommentList()
        if (newCommentList === null) {
            return
        }

        const newCommentReplyListDictionary: CommentReplyListDictionary = {}
        const promises: Promise<void>[] = []
        for (const openedCommentId of Array.from(openedCommentIds.values())) {
            const promise = this.getCommentReplyList(openedCommentId, false)
                .then(newReplyList => {
                    if (newReplyList === null) {
                        throw new Error('getCommentReplyListで通信エラー')
                    }
                    newCommentReplyListDictionary[openedCommentId] = newReplyList
                })
            promises.push(promise)
        }
        await Promise.all(promises).catch(() => {
            return
        })

        return {
            commentList: newCommentList,
            commentReplyListDictionary: newCommentReplyListDictionary,
        }
    }

    handleClickCancelButton = (): void => {
        this.closeInputArea()
    }

    renderViewRepliesButtonFunction = (comment: MeetingCommentView | DeletedCommentView): ReactNode => {
        return <SmallStyledLink onClick={() => this.handleClickViewRepliesButton(comment.meetingCommentId)}>
            <LeftRowFlexContainer>
                <IconBox><FontAwesomeIcon icon={faAngleDown}/></IconBox>
                {`返信を見る(${comment.replyCount}件)`}
            </LeftRowFlexContainer>
        </SmallStyledLink>
    }

    renderCloseRepliesButtonFunction = (comment: MeetingCommentView | DeletedCommentView): ReactNode => {
        return <SmallStyledLink onClick={() => this.handleClickCloseRepliesButton(comment.meetingCommentId)}>
            <LeftRowFlexContainer>
                <IconBox><FontAwesomeIcon icon={faAngleUp}/></IconBox>
                返信を閉じる
            </LeftRowFlexContainer>
        </SmallStyledLink>
    }

    renderReplyButtonFunction = (comment: MeetingCommentView | DeletedCommentView): ReactNode => {
        return <>
            <SmallStyledLink onClick={() => this.handleClickReplyButton(comment.meetingCommentId)}>
                <LeftRowFlexContainer>
                    <IconBox><FontAwesomeIcon icon={faReply}/></IconBox>
                    返信
                </LeftRowFlexContainer>
            </SmallStyledLink>
        </>
    }

    renderCommentList(): ReactNode {
        const {commentList} = this.state
        return <>{
            commentList.length === 0 ?
                <Box m={'15px 0 0 15px'}>投稿がありません。</Box>
                :
                commentList.map((meetingComment, index) => {
                    const {meetingCommentId} = meetingComment
                    return <div key={'meetingComment-' + meetingCommentId}>
                        {index !== 0 && <Divider/>}
                        {this.renderComment(meetingComment)}
                    </div>
                })
        }</>
    }

    renderComment(meetingComment: MeetingCommentView | DeletedCommentView): ReactNode {
        const {condominiumCd, meetingId, eventEmitter, havePostCommentAuthority} = this.props
        const {
            openedCommentIds,
            commentReplyListDictionary,
            inputCommentTarget,
            inputContent,
            inputFileList,
            deleteFileNumberList,
            commentInputError,
        } = this.state

        const {meetingCommentId, replyCount} = meetingComment

        const inputCommentContent: InputCommentContent = {
            inputContent,
            inputFileList,
            deleteFileNumberList,
            commentInputError,
        }
        const editCommentContent = this.isEditComment(inputCommentTarget) &&
        inputCommentTarget.meetingCommentId === meetingCommentId ?
            inputCommentContent : undefined

        const isReplyInputAreaOpened = inputCommentTarget === meetingCommentId
        const isCommentOpened = openedCommentIds.has(meetingCommentId)
        const renderActionAreaFunc = replyCount === 0 ?
            (!havePostCommentAuthority || isReplyInputAreaOpened) ?
                undefined :
                this.renderReplyButtonFunction :
            isCommentOpened ?
                this.renderCloseRepliesButtonFunction :
                this.renderViewRepliesButtonFunction
        const commentReplyList = commentReplyListDictionary[meetingCommentId]
        return <>
            <Box m={'10px 15px 11px 20px'}>
                <AdminMeetingCommentComponent
                    condominiumCd={condominiumCd}
                    meetingId={meetingId}
                    meetingComment={meetingComment}
                    sourceMeetingComment={meetingComment}
                    onClickReaction={this.handleClickReaction}
                    onClickCommentDelete={this.handleClickCommentDelete}
                    onClickEditButton={this.handleClickCommentEditButton}
                    editCommentContent={editCommentContent}
                    renderCommentEditArea={this.renderEditForm}
                    renderAdditionalAction={renderActionAreaFunc}
                    eventEmitter={eventEmitter}
                    havePostCommentAuthority={havePostCommentAuthority}
                    currentDatetime={this.props.currentDatetime}
                />
                {
                    isCommentOpened && commentReplyList &&
                    commentReplyList.map((commentReply, replyIndex) => {
                        const editCommentReplyContent = this.isEditCommentReply(inputCommentTarget) &&
                        inputCommentTarget.meetingCommentId === commentReply.meetingCommentId ?
                            inputCommentContent : undefined
                        const renderReplyButtonFunc =
                            (havePostCommentAuthority
                                && commentReplyList.length === replyIndex + 1
                                && !isReplyInputAreaOpened) ?
                                this.renderReplyButtonFunction : undefined
                        const showVerticalDivider =
                            commentReplyList.length !== replyIndex + 1 || havePostCommentAuthority
                        return <Box ml={'32px'} mt={replyIndex === 0 ? '5px' : '0px'}
                                    key={'meetingComment-' + commentReply.meetingCommentId}>
                            <AdminMeetingCommentComponent
                                condominiumCd={condominiumCd}
                                meetingId={meetingId}
                                meetingComment={commentReply}
                                sourceMeetingComment={meetingComment}
                                onClickReaction={this.handleClickReaction}
                                onClickCommentDelete={this.handleClickCommentDelete}
                                onClickEditButton={this.handleClickCommentEditButton}
                                editCommentContent={editCommentReplyContent}
                                renderCommentEditArea={this.renderEditForm}
                                renderAdditionalAction={renderReplyButtonFunc}
                                eventEmitter={eventEmitter}
                                havePostCommentAuthority={havePostCommentAuthority}
                                verticalDivider={showVerticalDivider}
                                currentDatetime={this.props.currentDatetime}
                            />
                        </Box>
                    })
                }
                {
                    isReplyInputAreaOpened &&
                    <Box mt={'3px'} ml={'42px'}>
                        {this.renderInputFormAndUserName('返信する')}
                    </Box>
                }
            </Box>
        </>
    }

    renderEditForm = (content: InputCommentContent): ReactNode => {
        return <AdminMeetingCommentInputForm
            submitButtonLabel={'完了'}
            onClickSubmitButton={this.handleClickSubmitButton}
            onClickCancelButton={this.handleClickCancelButton}
            onChange={this.handleChangeInputForm}
            content={content.inputContent}
            commentFileList={content.inputFileList}
            deleteFileNumberList={content.deleteFileNumberList}
            inputError={content.commentInputError}
        />
    }

    renderInputFormAndUserName(submitLabel: string): ReactNode {
        const {inputContent, inputFileList, deleteFileNumberList, commentInputError} = this.state

        const userName = MeetingHelper.createCommentUserName({
            familyName: SessionStorageHelper
                .getStringItemWithDefault(AdminLoginInfoSessionStorageKey.LOGIN_USER_FAMILY_NAME, ''),
            firstName: SessionStorageHelper
                .getStringItemWithDefault(AdminLoginInfoSessionStorageKey.LOGIN_USER_FIRST_NAME, ''),
            role: ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(
                SessionStorageHelper.getStringItemWithDefault(
                    AdminLoginInfoSessionStorageKey.LOGIN_USER_ROLE_ID,
                    ROLE_FOR_ADMINISTRATOR.ASSOC.value,
                ),
            ),
            roomList: [],
            isWithinTerm: false,
            hasMailAddress: false,
        })

        return <>
            <RowFlexContainer>
                <LeftRowFlexContainer>
                    <UserIconLarge/>
                    <Box width={'15px'}/>
                    <UserNameDiv>{userName}</UserNameDiv>
                </LeftRowFlexContainer>
            </RowFlexContainer>
            <Box mt={'10px'} ml={'47px'}>
                <AdminMeetingCommentInputForm
                    submitButtonLabel={submitLabel}
                    onClickSubmitButton={this.handleClickSubmitButton}
                    onClickCancelButton={this.handleClickCancelButton}
                    onChange={this.handleChangeInputForm}
                    content={inputContent}
                    commentFileList={inputFileList}
                    deleteFileNumberList={deleteFileNumberList}
                    inputError={commentInputError}
                />
            </Box>
        </>
    }

    render(): ReactNode {
        const {inputCommentTarget} = this.state
        return <>
            <Box p={'16px'}>コメント</Box>
            <Divider/>
            {this.props.havePostCommentAuthority &&
                <Box p={'30px 15px 11px 15px'}>
                    {
                        this.isNewComment(inputCommentTarget) ?
                            this.renderInputFormAndUserName('投稿する')
                            :
                            <AdminPrimaryOutlinedButton onClick={this.handleClickNewCommentButton}>
                                <CenterRowFlexContainer>
                                    <FontAwesomeIcon icon={faEdit}/>
                                    <Box ml={'15px'}>新しいコメントを投稿する</Box>
                                </CenterRowFlexContainer>
                            </AdminPrimaryOutlinedButton>
                    }
                </Box>}
            {this.renderCommentList()}
            <Box height={'30px'}/>
        </>
    }
}

const SmallStyledLink = styled(StyledLink)`
  &.MuiTypography-body2 {
    font-size: 12px;
  }
`
